import { actionTypes as dataFetchActionTypes } from '@buffer-mono/async-data-fetch'
import type { AnyAction } from '@reduxjs/toolkit'

import keyWrapper from '~publish/helpers/keywrapper'
import { actionTypes as profileActionTypes } from '~publish/legacy/profile-sidebar/reducer'

import { changeLinkShortener } from './thunks/changeLinkShortener'
import { getGoogleAnalyticsTrackingSettings } from './thunks/getGoogleAnalyticsTrackingSettings'
import { saveGoogleAnalyticsTrackingSettings } from './thunks/saveGoogleAnalyticsTrackingSettings'
import type { LinkShortener } from './types'

export const actionTypes = keyWrapper('GENERAL_SETTINGS', {
  CHANGE_SELECTED_LINK_SHORTENER: 0,
  TOGGLE_INSTAGRAM_REMINDERS: 0,
})

export type TrackingSettings = {
  utmCampaign: string
  utmMedium: string
  utmSource: string
}

export type GeneralSettingsState = {
  supportsMobileReminders: boolean
  isRemindersOnly: boolean
  googleAnalyticsEnabled: boolean
  remindersAreEnabled: boolean
  linkShortening: {
    linkShorteners: LinkShortener[]
    isBitlyConnected: boolean
  }
  trackingSettings?: TrackingSettings
}

const initialState: GeneralSettingsState = {
  supportsMobileReminders: false,
  isRemindersOnly: false,
  googleAnalyticsEnabled: false,
  remindersAreEnabled: false,
  linkShortening: {
    linkShorteners: [],
    isBitlyConnected: false,
  },
}

export default (
  state = initialState,
  action: AnyAction,
): GeneralSettingsState => {
  switch (action.type) {
    case profileActionTypes.SELECT_PROFILE:
      return {
        ...state,
        supportsMobileReminders:
          action.profile.service === 'instagram' ||
          // action.profile.service === 'tiktok' ||
          (action.profile.service === 'facebook' &&
            action.profile.service_type === 'group'),
        isRemindersOnly:
          (action.profile.service === 'facebook' &&
            action.profile.service_type === 'group') ||
          (action.profile.service === 'instagram' &&
            action.profile.service_type === 'personal'),
        googleAnalyticsEnabled:
          action.profile.googleAnalyticsEnabled === 'enabled',
        linkShortening: {
          linkShorteners: [],
          isBitlyConnected: false,
        },
      }
    case `getLinkShortener_${dataFetchActionTypes.FETCH_START}`:
      return {
        ...state,
        linkShortening: {
          linkShorteners: [],
          isBitlyConnected: false,
        },
      }
    case `${changeLinkShortener.fulfilled}`:
    case `getLinkShortener_${dataFetchActionTypes.FETCH_SUCCESS}`: {
      const linkShorteners = changeLinkShortener.fulfilled.match(action)
        ? action.payload
        : action.result.linkShorteners
      return {
        ...state,
        linkShortening: {
          linkShorteners,
          isBitlyConnected: linkShorteners.some(
            (linkShortener: LinkShortener) =>
              linkShortener.selected && linkShortener.domain === 'bit.ly',
          ),
        },
      }
    }
    case `${getGoogleAnalyticsTrackingSettings.fulfilled}`: {
      return {
        ...state,
        trackingSettings: action.payload,
      }
    }
    case `${saveGoogleAnalyticsTrackingSettings.fulfilled}`: {
      return {
        ...state,
        trackingSettings: {
          utmCampaign: action.payload.utmCampaign,
          utmMedium: action.payload.utmMedium,
          utmSource: action.payload.utmSource,
        },
      }
    }
    case `toggleInstagramReminders_${dataFetchActionTypes.FETCH_START}`:
      return {
        ...state,
        remindersAreEnabled: action.args.allowReminders,
      }
    case `toggleInstagramReminders_${dataFetchActionTypes.FETCH_FAIL}`:
      return {
        ...state,
        remindersAreEnabled: !state.remindersAreEnabled,
      }
    default:
      return state
  }
}

export const actions = {
  handleOnSelectLinkShortenerChange: ({
    profileId,
    domain,
  }: {
    profileId: string
    domain: string
  }): {
    type: typeof actionTypes.CHANGE_SELECTED_LINK_SHORTENER
    profileId: string
    domain: string
  } => ({
    type: actionTypes.CHANGE_SELECTED_LINK_SHORTENER,
    profileId,
    domain,
  }),
  handleOnShortenerChanged: ({
    profileId,
    result,
  }: {
    profileId: string
    result: { linkShorteners: LinkShortener[] }
  }): {
    type: string
    profileId: string
    result: { linkShorteners: LinkShortener[] }
  } => ({
    type: `changeLinkShortener_${dataFetchActionTypes.FETCH_SUCCESS}`,
    profileId,
    result,
  }),

  handleRemindersToggle: ({
    profileId,
    allowReminders,
  }: {
    profileId: string
    allowReminders: boolean
  }): {
    type: typeof actionTypes.TOGGLE_INSTAGRAM_REMINDERS
    profileId: string
    allowReminders: boolean
  } => ({
    type: actionTypes.TOGGLE_INSTAGRAM_REMINDERS,
    profileId,
    allowReminders,
  }),
}
